import type { Contact } from "../types";

export type SwipeTone = "positive" | "neutral" | "negative" | "special";

/** Direction an action is bound to. `down` is always Skip and never an action. */
export type SwipeDirection = "right" | "left" | "up";

/** The three gesture slots, in the order actions claim them. */
export const GESTURE_ORDER: readonly SwipeDirection[] = ["right", "left", "up"];

/**
 * Fields one answer writes. `null` clears a column — `undefined` would be
 * dropped before it reached PostgREST, so an action that means "unset this"
 * has to say `null`.
 *
 * A `string[]` value is an array column (`relationship_types`). The empty array
 * is a real answer there — "asked, and it plays no role" — and is NOT the same
 * as `null`, which means the question was never put.
 */
export type ContactPatch = Record<string, string | number | string[] | null>;

/**
 * One answer on a card.
 *
 * An action is a patch producer rather than a field/value pair, which is what
 * lets a scenario do something other than categorise: "push a week" writes a
 * date and a status, "restart this relationship" writes a play and schedules
 * the touch. Undo snapshots exactly the keys the patch returns, so a
 * multi-field action reverses as cleanly as a single-field one.
 */
export interface SwipeAction {
  /** Stable across label edits: tests and URLs refer to actions by id. */
  id: string;
  label: string;
  /** One line saying what the answer commits you to. */
  hint?: string;
  tone: SwipeTone;
  patch: (contact: Contact, now: Date) => ContactPatch;
}

export interface SwipeSort {
  field: string;
  /**
   * PostgREST maps ASC to `asc` (NULLS LAST) and DESC to `desc` (NULLS FIRST),
   * so a sort that has to put NULLs first cannot use ASC. That is why a
   * "never reviewed" backlog is expressed as a filter phase, not a sort.
   */
  order: "ASC" | "DESC";
}

/** `ra-data-postgrest` filter syntax (`field@operator`), which the FakeRest adapter also speaks. */
export type SwipeFilter = Record<string, unknown>;

/**
 * A filter, or one computed from the current time.
 *
 * "Due before now" and "quiet for 90 days" are filters that would go stale if
 * frozen at module load, so they are declared as functions and resolved by the
 * runner. The runner pins `now` for the life of the mount: a `now` that moved
 * every render would change the query key every render.
 */
export type SwipeFilterSpec = SwipeFilter | ((now: Date) => SwipeFilter);

export const resolveFilter = (spec: SwipeFilterSpec, now: Date): SwipeFilter =>
  typeof spec === "function" ? spec(now) : spec;

export interface SwipeQueryPhase {
  filter: SwipeFilterSpec;
  sort: SwipeSort;
}

/**
 * A scenario: which contacts to serve, and what a swipe does to them.
 *
 * The swipe interface is shared — every scenario runs through the same card,
 * the same gestures, the same undo. A scenario supplies only the two things
 * that differ: the filter that builds the queue and the actions bound to the
 * gestures. Adding one is a declaration here, not a change to the engine.
 */
export interface SwipeScenario {
  id: string;
  /** Short name, used in the picker and the URL. */
  title: string;
  /** The question the card asks. Phrased so an answer is one word. */
  question: string;
  /** One line in the picker: why working this scenario is worth the minutes. */
  rationale: string;
  /**
   * The open queue: contacts the question has never been answered for.
   * Runtime refinements are ANDed onto this, never replace it.
   */
  filter: SwipeFilterSpec;
  sort: SwipeSort;
  /**
   * Two to four. The first three claim right / left / up; a fourth is button
   * and number-key only, which is the ceiling before the deck starts lying
   * about how it is meant to be used.
   */
  actions: SwipeAction[];
  /**
   * Where the scenario goes once the open queue is empty, if anywhere.
   *
   * Scenarios over a column that was already populated (relationship strength,
   * pipeline stage, buying power were all bulk-set by migration) can only ask
   * "has this been *reviewed*?", which is what `last_triaged_at` records. Once
   * every contact has been reviewed once, the scenario reopens over the whole
   * table, oldest review first. A scenario whose column starts NULL has no
   * recycle phase: an answered question is answered.
   */
  recycle?: SwipeQueryPhase;
  /**
   * Refinement ids offered in this scenario's filter bar, in order. A scenario
   * only advertises the narrowings that make sense for its question — offering
   * "relationship level" on the relationship-level deck would be a loop.
   */
  refinements?: readonly string[];
}

/**
 * A narrowing a person can apply to a scenario at runtime, without a deploy.
 *
 * Selections live in the URL, so a scenario plus a set of refinements is a
 * link: "ICP fit, but only ecosystem contacts I have not touched in a year"
 * is a bookmark rather than a code change.
 */
export interface SwipeRefinement {
  id: string;
  label: string;
  choices: readonly { value: string; label: string }[];
  /** Filter fragment for a chosen value, ANDed onto the scenario's own filter. */
  toFilter: (value: string, now: Date) => SwipeFilter;
}
